/**
 * MeCycle — Phase 2 Women's Health
 *  /me/health/cycle
 *   • Today's cycle day banner (period / ovulation / fertile / PMS phase)
 *   • Quick log for today: flow · mood · pain · symptoms · pads used
 *   • Predicted next period + ovulation window + PMS
 *   • Sanitary-pad usage → suggests biohazard pickup
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { custApi, formatErr } from "../../lib/customerApi";
import {
  ArrowLeft, Drop, Heart, Moon, Sun, Users, Plus, Trash,
  ArrowRight, Calendar, ShoppingBag, Warning, Sparkle, Egg,
  CalendarBlank,
} from "@phosphor-icons/react";

const FLOW_META = {
  none:     { color: "#9CA3AF", bg: "#F3F4F6", label: "No flow" },
  spotting: { color: "#F59E0B", bg: "#FEF3C7", label: "Spotting" },
  light:    { color: "#EC4899", bg: "#FCE7F3", label: "Light" },
  medium:   { color: "#DC2626", bg: "#FEE2E2", label: "Medium" },
  heavy:    { color: "#7F1D1D", bg: "#FEE2E2", label: "Heavy" },
};

const MOODS = [
  { v: "happy",     l: "Happy",     e: "😊" },
  { v: "sad",       l: "Sad",       e: "😢" },
  { v: "anxious",   l: "Anxious",   e: "😟" },
  { v: "irritable", l: "Irritable", e: "😠" },
  { v: "calm",      l: "Calm",      e: "😌" },
  { v: "energetic", l: "Energetic", e: "⚡" },
  { v: "tired",     l: "Tired",     e: "😴" },
  { v: "sensitive", l: "Sensitive", e: "🥺" },
];

const SYMPTOMS = [
  "cramps", "headache", "bloating", "breast_tender", "back_pain",
  "nausea", "fatigue", "insomnia", "acne", "cravings",
  "dizziness", "hot_flash",
];

function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function prettyDate(iso) {
  if (!iso) return "—";
  return new Date(iso + "T00:00:00").toLocaleDateString(undefined, {
    weekday: "short", month: "short", day: "numeric",
  });
}
function daysBetween(a, b) {
  return Math.round((new Date(b + "T00:00:00") - new Date(a + "T00:00:00")) / 86400000);
}

function BackHeader({ to, title, subtitle }) {
  return (
    <div className="flex items-start gap-3">
      <Link to={to} className="w-9 h-9 rounded-full bg-white border border-gray-100 flex items-center justify-center shrink-0 hover:bg-gray-50">
        <ArrowLeft size={16} weight="bold" className="text-gray-700"/>
      </Link>
      <div className="flex-1 min-w-0">
        <h1 className="font-display font-black text-[22px] leading-tight tracking-tight text-[#111827]">
          {title}
        </h1>
        {subtitle && <div className="text-[12px] text-gray-500 mt-0.5">{subtitle}</div>}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// Main page
// ══════════════════════════════════════════════════════════════════
export function CycleTracker() {
  const [lovedOnes, setLovedOnes] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [summary, setSummary] = useState(null);
  const [logs, setLogs] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(blankForm());

  function blankForm() {
    return {
      date: todayISO(), flow: "none", mood: "", pain_level: "",
      symptoms: [], pads_used: "", notes: "", is_menopause: false,
    };
  }
  const toggleSymptom = (s) => setForm(f => ({
    ...f,
    symptoms: f.symptoms.includes(s) ? f.symptoms.filter(x => x !== s) : [...f.symptoms, s],
  }));

  const loadLO = async () => {
    try {
      const r = await custApi.get("/me/loved-ones");
      const list = r.data.loved_ones || [];
      setLovedOnes(list);
      const woman = list.find(l => l.kind === "woman" || l.kind === "pregnant") || list.find(l => l.kind === "self");
      if (woman && !selectedId) setSelectedId(woman.id);
      else if (list[0] && !selectedId) setSelectedId(list[0].id);
    } catch (e) { toast.error(formatErr(e)); }
  };
  const load = async () => {
    if (!selectedId) return;
    try {
      const [s, l] = await Promise.all([
        custApi.get(`/me/cycle/summary?loved_one_id=${selectedId}`),
        custApi.get(`/me/cycle/logs?loved_one_id=${selectedId}&days=90`),
      ]);
      setSummary(s.data);
      setLogs(l.data.logs || []);
      // Pre-fill today's log if it exists
      const today = todayISO();
      const existing = (l.data.logs || []).find(x => x.date === today);
      if (existing) {
        setForm({
          date: today, flow: existing.flow || "none",
          mood: existing.mood || "",
          pain_level: existing.pain_level == null ? "" : String(existing.pain_level),
          symptoms: existing.symptoms || [],
          pads_used: existing.pads_used == null ? "" : String(existing.pads_used),
          notes: existing.notes || "",
          is_menopause: !!existing.is_menopause,
        });
      } else {
        setForm(blankForm());
      }
    } catch (e) { toast.error(formatErr(e)); }
  };
  useEffect(() => { loadLO(); }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [selectedId]);

  const save = async () => {
    try {
      await custApi.post("/me/cycle/log", {
        loved_one_id: selectedId,
        date: form.date,
        flow: form.flow,
        mood: form.mood || null,
        pain_level: form.pain_level === "" ? null : Number(form.pain_level),
        symptoms: form.symptoms,
        pads_used: form.pads_used === "" ? null : Number(form.pads_used),
        notes: form.notes.trim() || null,
        is_menopause: !!form.is_menopause,
      });
      toast.success("Logged");
      setShowForm(false);
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };
  const removeLog = async (date) => {
    if (!window.confirm(`Delete log for ${date}?`)) return;
    try {
      await custApi.delete(`/me/cycle/log?loved_one_id=${selectedId}&date=${date}`);
      load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  // Compute current phase from summary
  const phase = useMemo(() => computePhase(summary, todayISO()), [summary]);

  const eligible = lovedOnes.filter(l => {
    // Only women / girls above 13 years old — exclude male, boy, others
    const kind = (l.kind || "self").toLowerCase();
    const gender = (l.gender || "").toLowerCase();
    if (["male", "other", "others"].includes(gender)) return false;
    if (l.age != null && l.age < 13) return false;
    // If kind is set, must be woman/pregnant/self; otherwise fall back to gender check
    if (kind && !["woman", "pregnant", "self"].includes(kind)) return false;
    return gender === "female" || kind === "woman" || kind === "pregnant" || kind === "self";
  });

  if (lovedOnes.length === 0) {
    return (
      <div className="space-y-4 px-4 py-4" data-testid="me-cycle">
        <BackHeader to="/me/health" title="Cycle tracker"/>
        <EmptyProfile/>
      </div>
    );
  }
  if (eligible.length === 0) {
    return (
      <div className="space-y-4 px-4 py-4" data-testid="me-cycle">
        <BackHeader to="/me/health" title="Cycle tracker"/>
        <div className="border border-dashed border-[#DC2626] bg-[#FEF7F6] rounded-md p-4 text-center">
          <div className="text-[12px] text-[#5C5A55]">
            No one tagged as <b>Woman</b> or <b>Self</b> yet.
            <br/>Set the Care Kind to <b>Woman</b> in Profile → Health Profile to start tracking.
          </div>
          <Link to="/me/profile#health-profile"
                className="mt-3 inline-block h-9 px-4 leading-9 bg-[#DC2626] hover:bg-[#B91C1C] text-white rounded-md text-xs font-medium">
            Go to Profile
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 px-4 py-4" data-testid="me-cycle">
      <BackHeader to="/me/health" title="Cycle tracker"
                    subtitle="Period · Ovulation · PMS · Menopause"/>

      {/* Quick link to Cycle Analyzer */}
      <Link to="/me/health/cycle/analysis"
            data-testid="cy-open-analysis"
            className="flex items-center justify-between rounded-2xl bg-white border border-[#F3E9EB] px-4 py-3 active:scale-[0.99]">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[#EC4899] font-bold">Cycle analysis</div>
          <div className="text-[13px] font-semibold text-[#111827] mt-0.5">See history · averages · trends</div>
        </div>
        <div className="w-8 h-8 rounded-full bg-[#FCE7F3] text-[#EC4899] inline-flex items-center justify-center font-bold">
          →
        </div>
      </Link>

      {/* Loved-one picker */}
      <div>
        <label className="text-[10px] uppercase tracking-widest text-[#5C5A55] block mb-1.5 font-semibold">
          For whom?
        </label>
        <div className="flex gap-2 overflow-x-auto pb-1 -mx-4 px-4">
          {eligible.map(lo => {
            const on = lo.id === selectedId;
            return (
              <button key={lo.id} onClick={() => setSelectedId(lo.id)}
                      data-testid={`cy-picker-${lo.id}`}
                      className={`h-10 px-3 shrink-0 rounded-full border text-xs font-medium inline-flex items-center gap-1.5 transition-colors ${
                        on ? "bg-[#DC2626] text-white border-[#DC2626]"
                           : "bg-white text-[#1A1A1A] border-[#E5E2D9]"
                      }`}>
                <span className={`w-6 h-6 rounded-full inline-flex items-center justify-center text-[10px] font-bold ${
                  on ? "bg-white/25" : "bg-[#FEE2E2] text-[#DC2626]"
                }`}>{lo.name?.[0]?.toUpperCase() || "?"}</span>
                {lo.name}
              </button>
            );
          })}
        </div>
      </div>

      {/* Quick setup — log last period start + cycle length */}
      {summary && selectedId && (
        <CycleSetupCard
          summary={summary}
          selectedId={selectedId}
          onSaved={load}
        />
      )}

      {/* Phase hero card */}
      {summary && (
        <div className="rounded-3xl p-5 relative overflow-hidden text-white shadow-[0_20px_50px_rgba(220,38,38,0.20)]"
             style={{ background: phase.gradient }}
             data-testid="cy-phase-card">
          <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-white/10 blur-2xl"/>
          <div className="relative">
            <div className="text-[10px] uppercase tracking-widest font-bold opacity-90 inline-flex items-center gap-1.5">
              <phase.Icon size={11} weight="fill"/> {phase.tag}
            </div>
            <div className="font-display font-black text-3xl leading-tight mt-1" data-testid="cy-phase-title">
              {phase.title}
            </div>
            <div className="text-[13px] opacity-95 mt-2 leading-snug">
              {phase.tip}
            </div>
            {phase.subline && (
              <div className="text-[11px] opacity-85 mt-3 font-mono">
                {phase.subline}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Iter 159 — Monthly calendar view (always visible) */}
      {summary && <CalendarGrid summary={summary} logs={logs}/>}

      {/* Predictions grid */}
      {summary && summary.next_period_start && (
        <div className="grid grid-cols-2 gap-2" data-testid="cy-predictions">
          <PredCard Icon={Drop} color="#DC2626" bg="#FEE2E2" label="Next period"
                    value={prettyDate(summary.next_period_start)}
                    hint={summary.days_until_next_period != null && summary.days_until_next_period >= 0
                          ? `in ${summary.days_until_next_period}d` : "overdue"}/>
          <PredCard Icon={Egg} color="#0F766E" bg="#CCFBF1" label="Ovulation"
                    value={prettyDate(summary.ovulation_day)}
                    hint="peak fertility"/>
          <PredCard Icon={Heart} color="#EC4899" bg="#FCE7F3" label="Fertile window"
                    value={`${prettyDate(summary.fertility_window?.start)} → ${prettyDate(summary.fertility_window?.end)}`}
                    hint="pregnancy chance ↑" wide/>
          <PredCard Icon={Moon} color="#7C3AED" bg="#EDE9FE" label="PMS window"
                    value={`${prettyDate(summary.pms_window?.start)} → ${prettyDate(summary.pms_window?.end)}`}
                    hint={`${summary.avg_cycle_length}d avg cycle`} wide/>
        </div>
      )}

      {/* Sanitary-waste hook — Rescigo unique */}
      {summary && summary.pads_used_this_cycle > 0 && (
        <div className="grid grid-cols-2 gap-2" data-testid="cy-waste">
          <div className="rounded-2xl p-3 bg-[#FEE2E2] border border-red-100">
            <ShoppingBag size={16} weight="duotone" className="text-[#DC2626]"/>
            <div className="font-display font-black text-lg text-[#DC2626] leading-none mt-1">
              {summary.pads_used_this_cycle}
            </div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-[#DC2626] mt-0.5">
              Pads this cycle
            </div>
          </div>
          <Link to="/me/requests?new=1" data-testid="cy-pickup-cta"
                className="rounded-2xl p-3 bg-gradient-to-br from-[#DC2626] to-[#7F1D1D] text-white active:scale-[0.97] transition-transform">
            <div className="text-[10px] uppercase tracking-widest font-bold opacity-90">Rescigo tip</div>
            <div className="text-[13px] font-semibold leading-tight mt-1">Book sanitary pickup →</div>
            <div className="text-[10px] opacity-90 mt-0.5">Discreet · sealed</div>
          </Link>
        </div>
      )}

      {/* Today's log */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-[0_4px_20px_rgba(0,0,0,0.03)] p-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-[#5C5A55] font-semibold">
              Today · {prettyDate(todayISO())}
            </div>
            <div className="text-[12px] text-gray-500">Quick actions &amp; detailed log</div>
          </div>
          {summary?.today_log && !showForm && (
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#DCFCE7] text-[#15803D] font-bold uppercase tracking-widest">
              Logged
            </span>
          )}
        </div>
        {!showForm ? (
          <div className="space-y-2">
            <PeriodQuickActions summary={summary} selectedId={selectedId} onSaved={load}/>
            <button onClick={() => setShowForm(true)}
                    data-testid="cy-log-open"
                    className="w-full h-10 border border-[#E5E2D9] hover:bg-gray-50 text-[#1A1A1A] rounded-md text-xs font-medium inline-flex items-center justify-center gap-1.5">
              <Plus size={12} weight="bold"/> {summary?.today_log ? "Edit today's log details" : "Add details (mood, symptoms, pads)"}
            </button>
          </div>
        ) : (
          <LogForm form={form} setForm={setForm}
                    toggleSymptom={toggleSymptom}
                    onSave={save} onCancel={() => { setShowForm(false); load(); }}/>
        )}
      </div>

      {/* Recent cycle history */}
      {summary && summary.recent_cycles?.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-widest text-gray-400 font-bold mb-2">
            Recent cycles
          </div>
          <div className="space-y-1.5" data-testid="cy-cycle-history">
            {[...summary.recent_cycles].reverse().map(c => (
              <div key={c.start} className="flex items-center gap-3 bg-white border border-gray-100 rounded-md p-2.5">
                <Drop size={16} className="text-[#DC2626]" weight="fill"/>
                <div className="flex-1 text-[12px]">
                  <div className="font-semibold text-[#1A1A1A]">{prettyDate(c.start)}{c.end !== c.start ? ` → ${prettyDate(c.end)}` : ""}</div>
                  <div className="text-[10px] text-gray-500">{c.length_days}-day period</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent logs list */}
      {logs.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-widest text-gray-400 font-bold mb-2">
            Recent logs ({logs.length})
          </div>
          <div className="space-y-1.5" data-testid="cy-log-list">
            {logs.slice(0, 15).map(l => {
              const fm = FLOW_META[l.flow || "none"];
              return (
                <div key={l.date} data-testid={`cy-log-${l.date}`}
                     className="flex items-center gap-3 border border-[#E5E2D9] rounded-md p-2.5">
                  <div className="w-8 h-8 rounded-full inline-flex items-center justify-center shrink-0"
                       style={{ backgroundColor: fm.bg, color: fm.color }}>
                    <Drop size={13} weight="fill"/>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[12px] font-semibold text-[#1A1A1A]">
                      {prettyDate(l.date)}
                      <span className="ml-2 text-[10px] uppercase tracking-widest font-normal" style={{ color: fm.color }}>
                        {fm.label}
                      </span>
                    </div>
                    <div className="text-[10px] text-gray-500 flex flex-wrap gap-1.5 mt-0.5">
                      {l.pain_level != null && <span>Pain {l.pain_level}/10</span>}
                      {l.pads_used ? <span>{l.pads_used} pads</span> : null}
                      {l.mood && <span>Mood: {l.mood}</span>}
                      {(l.symptoms || []).length > 0 && <span>· {l.symptoms.slice(0, 2).join(", ")}{l.symptoms.length > 2 ? "…" : ""}</span>}
                    </div>
                  </div>
                  <button onClick={() => removeLog(l.date)}
                          data-testid={`cy-del-${l.date}`}
                          className="p-1.5 rounded hover:bg-[#FEE2E2]">
                    <Trash size={12} color="#DC2626"/>
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function EmptyProfile() {
  return (
    <div className="border border-dashed border-[#DC2626] bg-[#FEF7F6] rounded-md p-4 text-center">
      <Users size={22} color="#DC2626" className="mx-auto mb-1"/>
      <div className="text-sm font-semibold text-[#1A1A1A]">Add a loved one first</div>
      <div className="text-[11px] text-[#5C5A55] mt-1">
        Head to Profile → Health Profile and add someone tagged as <b>Woman</b> or <b>Self</b>.
      </div>
      <Link to="/me/profile#health-profile"
            className="mt-3 inline-block h-9 px-4 leading-9 bg-[#DC2626] hover:bg-[#B91C1C] text-white rounded-md text-xs font-medium">
        Go to Profile
      </Link>
    </div>
  );
}

function PredCard({ Icon, color, bg, label, value, hint, wide }) {
  return (
    <div className={`rounded-2xl p-3 border ${wide ? "col-span-2" : ""}`}
         style={{ backgroundColor: bg, borderColor: color + "22" }}>
      <div className="flex items-center gap-1.5">
        <Icon size={14} weight="fill" color={color}/>
        <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color }}>{label}</div>
      </div>
      <div className="font-display font-black text-[15px] mt-1 leading-tight" style={{ color }}>
        {value}
      </div>
      <div className="text-[10px] mt-0.5 opacity-80" style={{ color }}>{hint}</div>
    </div>
  );
}

function LogForm({ form, setForm, toggleSymptom, onSave, onCancel }) {
  return (
    <div className="space-y-3" data-testid="cy-log-form">
      <div>
        <label className="text-[10px] uppercase tracking-widest text-[#5C5A55] block mb-1">Date</label>
        <input type="date" value={form.date}
               onChange={e => setForm(s => ({ ...s, date: e.target.value }))}
               data-testid="cy-date"
               className="w-full h-9 px-2 border border-[#E5E2D9] rounded text-sm"/>
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-widest text-[#5C5A55] block mb-1.5">Flow</label>
        <div className="grid grid-cols-5 gap-1.5">
          {Object.keys(FLOW_META).map(k => {
            const on = k === form.flow;
            const m = FLOW_META[k];
            return (
              <button key={k} type="button"
                      data-testid={`cy-flow-${k}`}
                      onClick={() => setForm(s => ({ ...s, flow: k }))}
                      className={`h-14 rounded-md text-[10px] font-semibold border transition-colors flex flex-col items-center justify-center leading-tight ${
                        on ? "text-white border-transparent" : "bg-white text-[#1A1A1A] border-[#E5E2D9]"
                      }`}
                      style={on ? { backgroundColor: m.color } : {}}>
                <Drop size={14} weight="fill"/>
                <span className="mt-0.5">{m.label}</span>
              </button>
            );
          })}
        </div>
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-widest text-[#5C5A55] block mb-1.5">Mood</label>
        <div className="flex flex-wrap gap-1.5">
          {MOODS.map(m => {
            const on = m.v === form.mood;
            return (
              <button key={m.v} type="button"
                      data-testid={`cy-mood-${m.v}`}
                      onClick={() => setForm(s => ({ ...s, mood: on ? "" : m.v }))}
                      className={`h-9 px-2.5 rounded-full text-[11px] font-medium border transition-colors ${
                        on ? "bg-[#DC2626] text-white border-[#DC2626]"
                           : "bg-white text-[#1A1A1A] border-[#E5E2D9]"
                      }`}>
                {m.e} {m.l}
              </button>
            );
          })}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className="text-[10px] uppercase tracking-widest text-[#5C5A55] block mb-1">Pain (0–10)</label>
          <input type="number" min="0" max="10" value={form.pain_level}
                 onChange={e => setForm(s => ({ ...s, pain_level: e.target.value }))}
                 data-testid="cy-pain"
                 className="w-full h-9 px-2 border border-[#E5E2D9] rounded text-sm"/>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-widest text-[#5C5A55] block mb-1">Pads used</label>
          <input type="number" min="0" max="30" value={form.pads_used}
                 onChange={e => setForm(s => ({ ...s, pads_used: e.target.value }))}
                 data-testid="cy-pads"
                 className="w-full h-9 px-2 border border-[#E5E2D9] rounded text-sm"/>
        </div>
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-widest text-[#5C5A55] block mb-1.5">Symptoms</label>
        <div className="flex flex-wrap gap-1.5">
          {SYMPTOMS.map(s => {
            const on = form.symptoms.includes(s);
            return (
              <button key={s} type="button"
                      data-testid={`cy-sym-${s}`}
                      onClick={() => toggleSymptom(s)}
                      className={`h-8 px-2.5 rounded-full text-[11px] font-medium border transition-colors ${
                        on ? "bg-[#DC2626] text-white border-[#DC2626]"
                           : "bg-white text-[#1A1A1A] border-[#E5E2D9]"
                      }`}>
                {on ? "✓ " : ""}{s.replace(/_/g, " ")}
              </button>
            );
          })}
        </div>
      </div>
      <div>
        <label className="text-[10px] uppercase tracking-widest text-[#5C5A55] block mb-1">Notes</label>
        <input value={form.notes}
               onChange={e => setForm(s => ({ ...s, notes: e.target.value }))}
               placeholder="Anything else"
               className="w-full h-9 px-2 border border-[#E5E2D9] rounded text-sm"/>
      </div>
      <label className="flex items-center gap-2 text-xs">
        <input type="checkbox" checked={form.is_menopause}
               onChange={e => setForm(s => ({ ...s, is_menopause: e.target.checked }))}
               data-testid="cy-menopause"/>
        In menopause / peri-menopause
      </label>
      <div className="grid grid-cols-2 gap-2 pt-1">
        <button onClick={onSave} data-testid="cy-save"
                className="h-10 w-full bg-[#DC2626] hover:bg-[#B91C1C] text-white rounded-md text-sm font-medium">
          Save entry
        </button>
        <button onClick={onCancel}
                className="h-10 w-full border border-[#E5E2D9] rounded-md text-sm">Cancel</button>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════
// Compute the phase card content from summary + today.
// ══════════════════════════════════════════════════════════════════
function computePhase(summary, today) {
  const DEFAULT = {
    Icon: Sparkle, tag: "Cycle tracker",
    title: "Start tracking",
    tip: "Log today's flow to unlock predictions and personalised tips.",
    subline: null,
    gradient: "linear-gradient(135deg,#DC2626 0%,#B91C1C 60%,#7F1D1D 100%)",
  };
  if (!summary) return DEFAULT;
  if (summary.today_log?.is_menopause) {
    return {
      Icon: Moon, tag: "Menopause phase",
      title: "You're in menopause",
      tip: "Symptom tracking helps your doctor tune HRT and lifestyle care.",
      subline: `Symptoms logged today: ${(summary.today_log.symptoms || []).length}`,
      gradient: "linear-gradient(135deg,#7C3AED 0%,#5B21B6 100%)",
    };
  }
  if (!summary.next_period_start) return {
    ...DEFAULT,
    title: "Start tracking your cycle",
    tip: "Add a period day to unlock ovulation & fertility predictions.",
  };

  const todayDate = new Date(today + "T00:00:00");
  const nextStart = new Date(summary.next_period_start + "T00:00:00");
  const daysToNext = Math.round((nextStart - todayDate) / 86400000);
  const startedThisCycle = summary.last_period_start
    ? daysBetween(summary.last_period_start, today) < (summary.avg_period_length || 5)
    : false;

  const inFertile = summary.fertility_window?.start &&
    today >= summary.fertility_window.start && today <= summary.fertility_window.end;
  const inPMS = summary.pms_window?.start &&
    today >= summary.pms_window.start && today <= summary.pms_window.end;
  const isOvulation = today === summary.ovulation_day;

  if (startedThisCycle && (summary.today_log?.flow || "none") !== "none") {
    return {
      Icon: Drop, tag: `Day ${summary.cycle_day_today || 1}`,
      title: "Period · take it easy",
      tip: "Iron-rich foods (spinach, dates, jaggery) + hydration help. Log pads used to track your usage.",
      subline: summary.next_period_start ? `Next expected around ${prettyDate(summary.next_period_start)}` : null,
      gradient: "linear-gradient(135deg,#DC2626 0%,#7F1D1D 100%)",
    };
  }
  if (isOvulation) {
    return {
      Icon: Egg, tag: "Ovulation day",
      title: "Peak fertility today",
      tip: "Highest pregnancy chance. If not planning, use protection.",
      subline: `Next period ${prettyDate(summary.next_period_start)} · ${daysToNext}d`,
      gradient: "linear-gradient(135deg,#0F766E 0%,#065F46 100%)",
    };
  }
  if (inFertile) {
    return {
      Icon: Heart, tag: "Fertile window",
      title: "Fertility window open",
      tip: "Higher chance of conception. Track cervical signs and mood.",
      subline: `Ovulation ${prettyDate(summary.ovulation_day)}`,
      gradient: "linear-gradient(135deg,#EC4899 0%,#BE185D 100%)",
    };
  }
  if (inPMS) {
    return {
      Icon: Moon, tag: "PMS phase",
      title: "PMS incoming",
      tip: "Mood swings, cravings and cramps are common. Sleep 8h, cut caffeine and log symptoms.",
      subline: `Period in ${daysToNext}d`,
      gradient: "linear-gradient(135deg,#7C3AED 0%,#5B21B6 100%)",
    };
  }
  // Follicular phase
  return {
    Icon: Sun, tag: `Day ${summary.cycle_day_today || 1}`,
    title: "Follicular phase",
    tip: "Energy is climbing. Good time for workouts and new projects.",
    subline: summary.ovulation_day ? `Ovulation in ${daysBetween(today, summary.ovulation_day)}d` : null,
    gradient: "linear-gradient(135deg,#F59E0B 0%,#B45309 100%)",
  };
}

// ══════════════════════════════════════════════════════════════════
// Iter 159 — Monthly calendar grid with color-coded phases
// Red = period, purple = fertile, teal ring = ovulation, orange = PMS
// ══════════════════════════════════════════════════════════════════
function CalendarGrid({ summary, logs }) {
  const [monthOffset, setMonthOffset] = useState(0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const first = new Date(today.getFullYear(), today.getMonth() + monthOffset, 1);
  const daysInMonth = new Date(first.getFullYear(), first.getMonth() + 1, 0).getDate();
  const startDow = first.getDay();
  const monthLabel = first.toLocaleDateString(undefined, { month: "long", year: "numeric" });

  const logMap = {};
  for (const l of (logs || [])) logMap[l.date] = l;

  const cells = [];
  for (let i = 0; i < startDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) {
    const iso = `${first.getFullYear()}-${String(first.getMonth() + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    cells.push({ d, iso });
  }

  const fertS = summary?.fertility_window?.start;
  const fertE = summary?.fertility_window?.end;
  const pmsS  = summary?.pms_window?.start;
  const pmsE  = summary?.pms_window?.end;
  const ovul  = summary?.ovulation_day;
  const nextP = summary?.next_period_start;
  const upcoming = summary?.upcoming_cycles || [];

  const classify = (iso) => {
    if (!iso) return null;
    const log = logMap[iso];
    if (log && log.flow && log.flow !== "none") return "period";
    if (nextP && iso === nextP) return "period-predicted";
    if (ovul && iso === ovul) return "ovulation";
    if (fertS && iso >= fertS && iso <= fertE) return "fertile";
    if (pmsS && iso >= pmsS && iso <= pmsE) return "pms";
    // Rolling future-month predictions
    for (const c of upcoming) {
      if (iso >= c.period_start && iso <= c.period_end) return "period-predicted";
      if (iso === c.ovulation_day) return "ovulation";
      if (iso >= c.fertility_start && iso <= c.fertility_end) return "fertile";
      if (iso >= c.pms_start && iso <= c.pms_end) return "pms";
    }
    return null;
  };

  const cellStyle = (t) => {
    switch (t) {
      case "period":            return { bg: "#DC2626", color: "white" };
      case "period-predicted":  return { bg: "#FEE2E2", color: "#DC2626", ring: "#DC2626" };
      case "fertile":           return { bg: "#EDE9FE", color: "#7C3AED" };
      case "ovulation":         return { bg: "#0F766E", color: "white", ring: "#0F766E" };
      case "pms":               return { bg: "#FFEDD5", color: "#EA580C" };
      default:                  return { bg: "transparent", color: "#374151" };
    }
  };

  const todayISOStr = todayISO();

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-[0_4px_20px_rgba(0,0,0,0.03)] p-4"
         data-testid="cy-calendar">
      <div className="flex items-center justify-between mb-3">
        <button onClick={() => setMonthOffset(m => m - 1)} data-testid="cy-cal-prev"
                className="w-8 h-8 rounded-full bg-[#F3F4F6] hover:bg-[#E5E7EB] inline-flex items-center justify-center">
          <ArrowLeft size={14}/>
        </button>
        <div className="text-center">
          <div className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">Calendar</div>
          <div className="font-display font-black text-sm text-[#1A1A1A]">{monthLabel}</div>
        </div>
        <button onClick={() => setMonthOffset(m => m + 1)} data-testid="cy-cal-next"
                className="w-8 h-8 rounded-full bg-[#F3F4F6] hover:bg-[#E5E7EB] inline-flex items-center justify-center">
          <ArrowRight size={14}/>
        </button>
      </div>
      {/* Week header */}
      <div className="grid grid-cols-7 gap-1 mb-1">
        {["S", "M", "T", "W", "T", "F", "S"].map((d, i) => (
          <div key={i} className="text-center text-[9px] uppercase tracking-widest text-gray-400 font-bold">
            {d}
          </div>
        ))}
      </div>
      {/* Cells */}
      <div className="grid grid-cols-7 gap-1">
        {cells.map((c, i) => {
          if (!c) return <div key={i} className="h-9"/>;
          const t = classify(c.iso);
          const s = cellStyle(t);
          const isToday = c.iso === todayISOStr;
          return (
            <div key={i}
                 data-testid={`cy-cal-day-${c.d}`}
                 className={`h-9 rounded-lg flex items-center justify-center text-[12px] font-semibold relative ${
                   isToday ? "ring-2 ring-offset-1 ring-[#111827]" : ""
                 }`}
                 style={{
                   backgroundColor: s.bg,
                   color: s.color,
                   ...(s.ring ? { boxShadow: `inset 0 0 0 2px ${s.ring}` } : {}),
                 }}>
              {c.d}
              {logMap[c.iso]?.pads_used > 0 && (
                <span className="absolute bottom-0.5 right-0.5 w-1 h-1 rounded-full bg-white opacity-90"/>
              )}
            </div>
          );
        })}
      </div>
      {/* Legend */}
      <div className="mt-3 flex flex-wrap gap-1.5 text-[10px]">
        <Legend color="#DC2626" label="Period"/>
        <Legend color="#EDE9FE" text="#7C3AED" label="Fertile"/>
        <Legend color="#0F766E" label="Ovulation"/>
        <Legend color="#FFEDD5" text="#EA580C" label="PMS"/>
        <Legend color="#FEE2E2" text="#DC2626" label="Next predicted" ring/>
      </div>
    </div>
  );
}

function Legend({ color, text, label, ring }) {
  return (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded"
          style={{ backgroundColor: text ? "#F9FAFB" : "transparent" }}>
      <span className="w-2.5 h-2.5 rounded-sm inline-block"
            style={{ backgroundColor: color, boxShadow: ring ? `inset 0 0 0 1.5px ${text || color}` : "none" }}/>
      <span style={{ color: text || "#374151" }} className="font-semibold">{label}</span>
    </span>
  );
}


/* ─── Start / Stop period quick actions ─── */
function PeriodQuickActions({ summary, selectedId, onSaved }) {
  const [busy, setBusy] = useState(false);
  const today = todayISO();
  const todayFlow = summary?.today_log?.flow || "none";
  const onPeriod = todayFlow !== "none";

  const startPeriod = async () => {
    setBusy(true);
    try {
      await custApi.post("/me/cycle/log", {
        loved_one_id: selectedId,
        date: today,
        flow: "medium",
      });
      toast.success("Period started today");
      onSaved && onSaved();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  const stopPeriod = async () => {
    if (!window.confirm("End the current period today? This will mark today as the last day and stop the flow.")) return;
    setBusy(true);
    try {
      // Setting today's flow to "none" ends the running cycle in
      // _extract_cycles_from_logs, so predictions kick in from the last period start.
      await custApi.post("/me/cycle/log", {
        loved_one_id: selectedId,
        date: today,
        flow: "none",
      });
      toast.success("Period ended");
      onSaved && onSaved();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="grid grid-cols-2 gap-2" data-testid="cy-period-actions">
      <button onClick={startPeriod} disabled={busy || onPeriod}
              data-testid="cy-period-start"
              className="h-11 rounded-md text-sm font-semibold inline-flex items-center justify-center gap-1.5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-[#DC2626] hover:bg-[#B91C1C] text-white">
        <Drop size={14} weight="fill"/>
        {onPeriod ? "Period started" : "Start period today"}
      </button>
      <button onClick={stopPeriod} disabled={busy || !onPeriod}
              data-testid="cy-period-stop"
              className="h-11 rounded-md text-sm font-semibold inline-flex items-center justify-center gap-1.5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed border border-[#DC2626] text-[#DC2626] bg-white hover:bg-[#FEE2E2]">
        <Drop size={14}/>
        Stop period today
      </button>
    </div>
  );
}


/* ─── Cycle setup card — log last period start + cycle length ─── */
function CycleSetupCard({ summary, selectedId, onSaved }) {
  const [open, setOpen] = useState(!summary?.last_period_start);
  const [lastStart, setLastStart] = useState(summary?.last_period_start || "");
  const [cycleLen, setCycleLen] = useState(summary?.pref_cycle_length || summary?.avg_cycle_length || 28);
  const [periodLen, setPeriodLen] = useState(summary?.pref_period_length || summary?.avg_period_length || 5);
  const [busy, setBusy] = useState(false);

  // Reset local state when the summary changes (e.g. loved-one switch)
  useEffect(() => {
    setLastStart(summary?.last_period_start || "");
    setCycleLen(summary?.pref_cycle_length || summary?.avg_cycle_length || 28);
    setPeriodLen(summary?.pref_period_length || summary?.avg_period_length || 5);
  }, [summary?.last_period_start, summary?.avg_cycle_length, summary?.avg_period_length,
      summary?.pref_cycle_length, summary?.pref_period_length]);

  const save = async () => {
    if (!lastStart) return toast.error("Pick your last period start date");
    setBusy(true);
    try {
      // 1. Persist cycle-length preferences on the loved-one document FIRST
      //    so the summary respects user input even with a single log.
      await custApi.patch(`/me/loved-ones/${selectedId}`, {
        cycle_length_days: Number(cycleLen),
        period_length_days: Number(periodLen),
      });
      // 2. Log the period-start day (only). Additional day-by-day logging
      //    stays with the user via Start/Stop period actions so we don't
      //    fabricate false cycle boundaries.
      await custApi.post("/me/cycle/log", {
        loved_one_id: selectedId,
        date: lastStart,
        flow: "medium",
      });

      toast.success("Cycle set up — check your predictions below");
      setOpen(false);
      onSaved && onSaved();
    } catch (e) {
      toast.error(formatErr(e));
    } finally { setBusy(false); }
  };

  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)}
        data-testid="cy-setup-open"
        className="w-full text-left rounded-2xl p-3 bg-white border border-[#E5E2D9] flex items-center gap-3 active:scale-[0.995]">
        <div className="w-10 h-10 rounded-xl bg-[#FEE2E2] text-[#DC2626] inline-flex items-center justify-center shrink-0">
          <Drop size={18} weight="fill"/>
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[13px] font-bold text-[#111827] leading-tight">Update cycle</div>
          <div className="text-[11px] text-[#5C5A55] mt-0.5">
            Last period {summary?.last_period_start || "—"} · {summary?.avg_cycle_length || 28}d cycle · {summary?.avg_period_length || 5}d duration
          </div>
        </div>
        <div className="text-[#DC2626] text-[11px] font-bold">Edit</div>
      </button>
    );
  }
  return (
    <div className="rounded-2xl p-4 bg-white border-2 border-dashed border-[#DC2626] space-y-3"
         data-testid="cy-setup-form">
      <div className="flex items-center gap-2">
        <Drop size={16} weight="fill" className="text-[#DC2626]"/>
        <div className="text-[13px] font-bold text-[#111827]">Set up cycle tracking</div>
      </div>
      <label className="block">
        <div className="text-[10px] uppercase tracking-[0.18em] text-[#5C5A55] font-bold mb-1">Last period start date</div>
        <input type="date" value={lastStart}
               onChange={e => setLastStart(e.target.value)}
               max={new Date().toISOString().slice(0, 10)}
               data-testid="cy-setup-last-start"
               className="w-full h-11 px-3 border border-[#E5E2D9] rounded-md text-sm"/>
      </label>
      <div className="grid grid-cols-2 gap-3">
        <label className="block">
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#5C5A55] font-bold mb-1">Cycle length (days)</div>
          <input type="number" min="20" max="45" value={cycleLen}
                 onChange={e => setCycleLen(e.target.value)}
                 data-testid="cy-setup-cycle-len"
                 className="w-full h-11 px-3 border border-[#E5E2D9] rounded-md text-sm"/>
          <div className="text-[10px] text-[#8A8780] mt-1">Default 28 · tap to change</div>
        </label>
        <label className="block">
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#5C5A55] font-bold mb-1">Period duration (days)</div>
          <input type="number" min="2" max="10" value={periodLen}
                 onChange={e => setPeriodLen(e.target.value)}
                 data-testid="cy-setup-period-len"
                 className="w-full h-11 px-3 border border-[#E5E2D9] rounded-md text-sm"/>
          <div className="text-[10px] text-[#8A8780] mt-1">Usually 3–7 days</div>
        </label>
      </div>
      <div className="flex gap-2">
        <button onClick={save} disabled={busy}
          data-testid="cy-setup-save"
          className="flex-1 h-11 bg-[#DC2626] hover:bg-[#B91C1C] text-white rounded-2xl text-sm font-bold disabled:opacity-50">
          {busy ? "Saving…" : "Save & show predictions"}
        </button>
        <button onClick={() => setOpen(false)}
          className="h-11 px-4 border border-[#E5E2D9] rounded-2xl text-sm font-semibold">
          Cancel
        </button>
      </div>
    </div>
  );
}
